import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  Box,
  Slider,
  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
} from '@mui/icons-material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  // Save as SaveIcon,
  RestartAlt as RestartIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { RHFTextField, RHFSelectField, RHFCheckbox, FormProvider } from '../../common/hook-form';
// import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { apiConstants, postGeneric } from '../../common/constants';
import { getUserName } from '../../helpers/ReduxHelpers';
import MUIModal from '../../components/MuiModal';
import { ViewModule as ViewModuleIcon, Palette as PaletteIcon, Settings as SettingsIcon, Upload as UploadIcon, TextFields as TextFieldsIcon, ExpandMore as ExpandMoreIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import LabelConfig from './LabelConfig';
import { fillPreviewData } from './labelPreview';

// Light-blue info callout used at the top of each section to explain what it does.
const InfoCallout = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.25, mb: 2.5, borderRadius: '10px', bgcolor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#1E40AF', mt: '1px', flex: '0 0 auto' }} />
    <Typography sx={{ fontSize: 12.5, color: '#1E40AF', lineHeight: 1.5 }}>{children}</Typography>
  </Box>
);

// Small muted caption placed under a field to explain what it controls.
const Hint = ({ children }) => (
  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5, lineHeight: 1.4 }}>{children}</Typography>
);

// A small uppercase group heading inside a section.
const GroupLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#94a3b8', mb: 1 }}>{children}</Typography>
);

// Replaces the nested tab bar: each of the three settings groups is now a stacked,
// expandable section with an icon, title and a one-line description of what it does.
const SectionAccordion = ({ icon, title, desc, defaultExpanded, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{ border: '1px solid #EAECEF', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.25, my: 1.25 } }}>
      <Box sx={{ width: 34, height: 34, borderRadius: '9px', flex: '0 0 auto', display: 'grid', placeItems: 'center', bgcolor: 'rgba(21,101,192,0.09)', color: '#1565c0' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#212B36', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, color: '#637381' }}>{desc}</Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ borderTop: '1px solid #F1F3F7', pt: 2.5, px: 2 }}>{children}</AccordionDetails>
  </Accordion>
);

const DEFAULT_VALUES = {
  // Layout Settings
  labelSize: 'A6',
  orientation: 'portrait',
  margins: 5,

  // Content Visibility
  showMerchantLogo: true,
  showCourierName: true,
  showSoldBy: true,
  showGSTIN: true,
  showReturnAddress: true,
  showReturnContact: true,
  // Delivery address is ON by default and its switch is shown to everyone, so anyone can
  // turn it off. The delivery-contact switch is OFF by default and only shown to the
  // allowlisted users (see CONTACT_TOGGLE_ALLOWED_EMAILS); for everyone else it stays OFF
  // with no way to flip it, so their templates carry no consignee phone number.
  showDeliveryAddress: true,
  showDropContact: false,
  showBarcode: true,
  showBarCode2: true,
  awbNumber: true,
  showOrderId: true,
  showOrderDate: true,
  showOrderName: false,
  showinvoiceValue: true,
  shippingMode: true,
  showServiceType: true,
  showDeadWeight: false,
  showDimensions: false,
  showPrepaidAttributeHeader: false,
  bottomText: true,
  showDescription: true,
  showSKU: true,
  showQty: true,

  // Typography Settings
  titleFontSize: 10,
  bodyFontSize: 9,
  headerFontSize: 12,
  fontFamily: 'Open Sans',

  // Spacing Settings
  elementSpacing: 5,
  sectionSpacing: 8,

  // Custom Text
  customHeaderText: '',
  customFooterText: 'Powered By Proship',
  customTextAboveFooter: '',
  customTextPosition: 'above-footer',
  customTextFontSize: 8,
  customTextBold: false,
  customTextUppercase: false,
  customTextAlign: 'left',

  // Position Settings
  barcodePosition: 'center',
  qrCodePosition: 'bottom-right',
  // Where the Order ID / Order Name lines print: 'top' = header (beside the logo),
  // 'bottom' = the handover block next to Tracking ID (the legacy spot).
  orderIdPosition: 'top',
  orderNamePosition: 'bottom',

  // Size Settings
  logoSize: 50,
  barcodeHeight: 50,
  qrCodeSize: 80,
  barcodeLayout: 'stacked',
  awbFontSize: 14,

  // Template Selection
  selectedTemplateId: '',
};

// Only the delivery-contact (consignee phone) switch is restricted — printing a customer's
// phone number on a label is the sensitive part. The delivery-address switch is available to
// everyone. Non-allowlisted users never see the contact switch, so it stays OFF (DEFAULT_VALUES)
// and their templates carry no consignee phone number.
const CONTACT_TOGGLE_ALLOWED_EMAILS = ['ankit.mishra@prozo.com', 'shiva.awasthi@prozo.com', 'devansh.richharia@prozo.com'];
const RESTRICTED_TOGGLES = ['showDropContact'];

// Content toggles grouped into readable categories (instead of one flat wall of checkboxes).
const CONTENT_GROUPS = [
  { title: 'Branding & Header', items: [['showMerchantLogo', 'Merchant logo'], ['showSoldBy', "'Sold By' info"], ['showGSTIN', 'GSTIN'], ['showPrepaidAttributeHeader', 'Prepaid attribute header'], ['bottomText', 'Bottom / footer text']] },
  { title: 'Addresses & Contact', items: [['showDeliveryAddress', 'Delivery address'], ['showDropContact', 'Delivery contact number'], ['showReturnAddress', 'Return address'], ['showReturnContact', 'Return contact']] },
  { title: 'Courier & Shipping', items: [['showCourierName', 'Courier name'], ['shippingMode', 'Shipping mode'], ['showServiceType', 'Service type'], ['showDeadWeight', 'Dead weight'], ['showDimensions', 'Dimensions']] },
  { title: 'Barcodes & IDs', items: [['showBarcode', 'Barcode'], ['showBarCode2', 'Barcode 2'], ['awbNumber', 'Tracking number'], ['showOrderId', 'Order ID'], ['showOrderName', 'Order Name (Channel)'], ['showOrderDate', 'Order date'], ['showinvoiceValue', 'Invoice value']] },
  { title: 'Item Table', items: [['showDescription', 'Description column'], ['showSKU', 'SKU column'], ['showQty', 'Qty column']] },
];

const LabelDIY = () => {
  const { enqueueSnackbar } = useSnackbar();
  // Delivery address defaults ON and its switch is visible to everyone, so anyone can turn it
  // off. Delivery contact defaults OFF and only the allowlisted users see its switch, so
  // everyone else's templates carry no consignee phone number.
  const canToggleContact = CONTACT_TOGGLE_ALLOWED_EMAILS.includes(String(getUserName() || '').trim().toLowerCase());
  const visibleContentGroups = canToggleContact
    ? CONTENT_GROUPS
    : CONTENT_GROUPS
      .map((g) => ({ ...g, items: g.items.filter(([name]) => !RESTRICTED_TOGGLES.includes(name)) }))
      .filter((g) => g.items.length);
  const [previewOpen, setPreviewOpen] = useState(false);
  // const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [openUploadModal, setOpenUploadModal] = useState(false);

  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const { watch, setValue, handleSubmit, reset } = methods;
  const watchedValues = watch();


  const handlePreview = () => {
    setPreviewData(watchedValues);
    setPreviewOpen(true);
  };

  const onSubmit = async (data) => {
    // setSaving(true);
    try {
      // Generate HTML template based on settings
      const htmlTemplate = generateHTMLTemplate(data);

      const payload = {
        owner: data.merchantId || data.courierId,
        type: data.merchantId ? 'MERCHANT' : 'COURIER',
        size: data.labelSize,
        template: htmlTemplate,
        isDefault: false,
        templateName: data.templateName || 'Custom Template',
        // Store the settings object for future editing
        settings: data
      };
      await postGeneric(apiConstants.UPLOAD_LABEL_TEMPLATE_MULTIPLECOURIER, payload);
      enqueueSnackbar('Template saved successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    } finally {
      // setSaving(false);
    }
  };

  const generateHTMLTemplate = (settings) => {
    // Valid CSS @page size per label size. Thymeleaf does not substitute [[${pageSize}]]
    // inside <style>, so a literal must not reach iText's PageSizeParser. Actual page size
    // is set programmatically on the backend; this value is only there to keep the CSS valid.
    const pageSizeCss = { A6: 'A6', A4: 'A4', A4_LANDSCAPE: 'landscape', A6X4: '6in 4in' }[settings.labelSize] || 'A6';
    const customTextBlock = settings.customTextAboveFooter
      ? `<div style="text-align: ${settings.customTextAlign || 'left'};margin-top:8px;margin-bottom:4px;padding:0 5px;">
          <span style="display: block; font-size:${settings.customTextFontSize || 8}px;line-height:1.3;color:#333;${settings.customTextBold ? 'font-weight:700;' : ''}${settings.customTextUppercase ? 'text-transform:uppercase;' : ''}">${settings.customTextAboveFooter}</span>
        </div>`
      : '';
    const ct = (pos) => settings.customTextPosition === pos ? customTextBlock : '';

    // Order ID / Order Name lines — same markup wherever they print; the position
    // settings decide the slot: 'top' = header (left cell, beside the logo),
    // 'bottom' = the handover block next to Tracking ID (the legacy spot).
    const orderIdLine = settings.showOrderId ? `<div style="padding:0;"><strong>Order ID:</strong> <span th:text="\${order.shippingLabelId}"></span></div>` : '';
    const orderNameLine = settings.showOrderName ? `<div style="padding:0;" th:if="\${order.channelOrderName}"><strong>Order Name:</strong> <span th:text="\${order.channelOrderName}"></span></div>` : '';
    const idsAt = (pos) => `${settings.orderIdPosition === pos ? orderIdLine : ''}${settings.orderNamePosition === pos ? orderNameLine : ''}`;

    return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/html">
<head>
  <meta charset="utf-8" />
  <title>Shipping Label</title>
 <style>

    html {
      margin: 0px
    }

    body {
      font-family: "Open Sans", sans-serif;
      font-size: 9px;
      line-height: 14px;
      padding: 0;
      margin: 0px;
    }

    @page {
      size: ${pageSizeCss};
      margin: 5px;
    }


    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    /*.wrapper{
                width: 100%;
                height: 100%;
                background-color: #000;
            }*/
    .container {
      width: 100%;
      height: 100%;
      background-color: #fff;
      padding: 0;
      box-sizing: border-box;
      margin: auto;
    }

    .box {
      width: 100%;
      height: auto;
      text-align: left;
      margin: 0px auto;
      box-sizing: border-box;
      padding: 0 2px;
      border: 2px dashed #333;
      border-width: 0 2px 0 2px;
    }

    ${settings.showPrepaidAttributeHeader ? `
    .codAmount {
      background-color: #eaeaea;
      font-weight: 700;
      padding: 2px 10px;
      font-size: 9px;
      flex: 1;
      padding: 0;
    }

    .codRow {
      background-color: #eaeaea;
      display: flex;
      align-items: center;
    }

    .partialPrepaid {
      background-color: #eaeaea;
      font-size: 9px;
      font-weight: 700;
      margin-left: 6px;
    }
    ` : `
    .codAmount {
      background-color: #eaeaea;
      font-weight: 700;
      padding: 2px 10px;
      font-size: 9px;
      padding: 0;
    }
    `}

    .panel {
      width: 100%;
      height: auto;
      padding: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.5);
    }

    .address {
      height: auto;
      min-height: auto;
    }

    .panel p {
      padding-left: 5px;
    }

    hr {
      border: 0.5px solid rgba(0, 0, 0, 0.5);
    }

    .uppercase {
      text-transform: uppercase;
    }

    .responsive-image {
      width: 240px;
      height: 100px;
      display: block;
      margin: auto;
    }

    .border-bottom-none {
      border-bottom: none;
    }

    .gray {
      background-color: #eaeaea;
    }

    table {
      width: 100%;
      border-spacing: 0;
      border-collapse: collapse;
      border: 1px solid #000;
      margin-bottom: 0px;
      padding: 0;
    }

    tbody td,
    thead th,
    tfoot td {
      padding: 0 5px;
      border: 1px solid #333;
    }

    tfoot td {
      font-weight: 700;
    }

    .product {
      margin-bottom: 0;
      margin-top: 0;
    }

    .direction {
      width: 100%;
      overflow: hidden;
      background-color: #eaeaea;
      border: 1px solid rgba(0, 0, 0, 0.5);
      border-top-width: 0;
      margin-bottom: 5px;
    }

    .direction p {
      float: right;
      border-left: 1px solid rgba(0, 0, 0, 0.5);
      padding: 0 5px;
      font-weight: 900;
      font-size: 9px;
    }

    .black-box {
      color: #fff;
      background-color: #000;
      display: inline-block;
      padding: 0 2px;
      width: auto;
      float: left;
      min-width: 100px;
    }

    .pull-left {
      float: left;
    }

    .pull-right {
      float: right;
    }

    .block {
      overflow: hidden;
    }

    .padding-5 {
      padding: 5px;
    }

    .margin-top-5 {
      margin-top: 5px;
    }

    .padding-bottom-5 {
      margin-bottom: 5px;
    }

    .clearFix {
      clear: both;
    }

    .logo {
      color: #4a5874;
      font-size: 9px !important;
      letter-spacing: 2px;
      font-family: "Open Sans", sans-serif;
      text-align: right;
      width: 100%;
      padding-right: 5px;
    }

    .logo span {
      display: inline-block;
      width: 50px;
      letter-spacing: 0.5px;
      font-family: "Open Sans", sans-serif;
    }

    .logo span:nth-child(2) {
      font-weight: 700;
      width: 40px;
      margin-left: 8px;
    }

    .invoice {
      width: 94%;
      margin: auto;
      border-top: 2px dashed #333;
      box-sizing: border-box;
      padding: 5px 2%;
    }

    .parent {
      border-bottom: 2px solid #efefef;
      overflow: hidden;
    }

    .parent .child {
      width: 25%;
      float: left;
      padding-left: 5px;
      border-left: 2px solid #efefef;
    }

    .child:first-child {
      border-left: none;
    }

    .order-detail {
      width: 100%;
      overflow: hidden;
    }

    .order-detail .child {
      width: 50%;
      float: left;
      padding: 5px;
    }

    .order-detail .child h4 {
      color: #454648;
    }

    .order-detail .child p {
      font-size: 9px;
    }

    .order-table tbody tr:last-child,
    .order-table thead th,
    .order-table tfoot td {
      border-width: 0 0 1px 0;
      border-style: solid;
      border-color: #000;
    }

    /*.order-table tbody tr{
         height: 25px;
         }*/
    .order-table tbody td {
      border: none;
      text-align: center;
    }

    .order-table tbody td:nth-child(1) {
      width: 45%;
      text-align: left;
    }

    .product tbody tr td:nth-child(5) {
      display: none;
    }

    .product tbody tr td:nth-child(6) {
      display: none;
    }

    .product tbody tr td:nth-child(4) {
      display: none;
    }

    .text-center {
      text-align: center;
    }

    .text-right {
      text-align: right;
    }

    .trans-table td {
      border-color: transparent;
    }

    .sort-code {
      word-break: break-word;
      font-size: 17px;
      line-height: 28px;
      text-align: right;
    }
    
    .delivery-title {
      font-weight: 700;
      font-size: 10px;
      margin-bottom: 4px;
      text-transform: uppercase;
      border-bottom: 1px solid #eaeaea;
      padding-bottom: 2px;
    }
    .delivery-details {
      font-size: 9px;
      line-height: 1.1;
    }

    .customer-name {
      font-weight: 700;
      margin: 0 0 1px 0;
    }

    .address-line, .contact-info {
      margin: 0 0 1px 0;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    .pincode {
      font-weight: 700;
      background: #eaeaea;
      padding: 0 2px;
    }  
  </style>
</head>

<body>
  ${settings.showDeliveryAddress ? '<!-- proship:show-delivery-address -->' : ''}
  ${settings.showDropContact ? '<!-- proship:show-delivery-phone -->' : ''}
  <div class="wrapper">
    <div class="container">
      ${settings.enableWatermark ? `<div class="watermark">${settings.watermarkText}</div>` : ''}
      <div class="box">
        ${ct('after-header')}
        <table class="trans-table">
          <tbody>
            <tr>
              <td style="width: 50%">
                <p style="font-size: ${settings.bodyFontSize}px; font-weight: 700">
                  ${settings.customHeaderText || ''}
                </p>
                ${idsAt('top')}
              </td>
              <td style="width: 50%" align="right">
                                 ${settings.showMerchantLogo ? `
                   <img style="height: ${settings.logoSize}%; width: ${settings.logoSize}%; margin-right: 1rem"
                        th:if="\${order.logo}"
                        th:src="\${order.logo}" alt="logo" />
                 ` : ''}
              </td>
            </tr>
          </tbody>
        </table>

        ${settings.shippingMode ? (settings.showPrepaidAttributeHeader ? `
          <div class="codRow">
            <p class="codAmount">
              <span th:text="\${order.paymentMode}"></span>
            </p>
            <span class="partialPrepaid" th:if="\${order.isPartialPrepaid}">
              Partial Prepaid
            </span>
          </div>
        ` : `
          <p class="codAmount">
            <span th:text="\${order.paymentMode}"></span>
          </p>
        `) : ''}
        ${ct('after-shipping-mode')}

        <div class="address panel">
          <table class="trans-table">
            <tbody>
              <tr>
                <td style="width: 50%; vertical-align: top;">
                  <div class="delivery-details">
                    ${settings.showDeliveryAddress ? `
                      <strong>DELIVERY ADDRESS:</strong>
                      <p class="customer-name"><span th:text="\${order.dropName}"></span></p>
                      <p class="address-line"><span th:text="\${order.dropAddress}"></span><br/>
                      <span th:text="\${order.dropCity}"></span> |
                      <span th:text="\${order.dropState}"></span> |
                      <span class="pincode" th:text="\${order.dropPincode}"></span></p>
                    ` : ''}
                    ${settings.showDropContact ? `
                      <p class="address-line" th:if="\${order.dropContactNumber}"><strong>Contact:</strong> <span th:text="\${order.dropContactNumber}"></span></p>
                    ` : ''}
                  </div>
                </td>
                <td style="width: 50%">
                  <p class="text-right">
                    ${settings.showOrderDate ? `
                      Order Date: <strong><span th:text="\${order.awbRegisteredDate}"></span></strong>
                    ` : ''}
                  </p>
                  ${settings.showOrderDate ? `
                    <p class="sort-code"><span th:text="\${order.sortCode}"></span></p>
                  ` : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        ${ct('after-delivery-address')}

        <div class="panel">
          <table class="trans-table gray">
            <tr>
              <td style="width: 60%; vertical-align: top;">
                ${settings.awbNumber ? `<p style="margin: 2px 0;"><strong>Courier AWB No:</strong> <span th:text="\${order.awbNumber}"></span></p>` : ''}
                ${settings.showCourierName ? `<p style="margin: 2px 0;"><strong>Courier Name:</strong> <span th:text="\${order.courierName}"></span></p>` : ''}
                ${settings.showDeadWeight ? `<p style="margin: 2px 0;"><strong>Dead Weight:</strong> <span th:text="\${order.deadWeight}"></span> kg</p>` : ''}
                ${settings.showDimensions ? `<p style="margin: 2px 0;"><strong>Dimensions:</strong> <span th:text="\${order.length}"></span> x <span th:text="\${order.breadth}"></span> x <span th:text="\${order.height}"></span> cm</p>` : ''}
              </td>
              <td style="width: 40%; vertical-align: top;">
                ${settings.showinvoiceValue ? `<p style="margin: 2px 0;"><strong>Total Price:</strong> <span th:text="\${order.invoiceValue}"></span></p>` : ''}
                ${settings.shippingMode ? `<p style="margin: 2px 0;">Mode of Shipping: <b><span th:text="\${order.shippingMode}"></span></b></p>` : ''}
                ${settings.showServiceType ? `<p style="margin: 2px 0;" th:if="\${order.serviceType}">Service Type: <b><span th:text="\${order.serviceType}"></span></b></p>` : ''}
              </td>
            </tr>
          </table>
        </div>
        ${ct('after-awb-info')}

        ${settings.showBarcode ? `
          ${settings.barcodeLayout === 'side-by-side' ? `
            <div class="panel" style="margin-top: 4px;">
              <table class="trans-table">
                <tr>
                  <td style="width: 50%; text-align: center;">
                    <p><span><img style="display: block;width: 100%; height: auto; max-height: ${settings.barcodeHeight}px;" th:src="\${order.barcode}" alt="Barcode" /></span></p>
                    <p style="font-size: ${settings.awbFontSize || 14}px; font-weight: 700; letter-spacing: 0.5px;">
                      <b><span th:text="\${order.awbNumber}"></span></b>
                    </p>
                  </td>
                  ${settings.showBarCode2 ? `
                    <td style="width: 50%; text-align: center;">
                      <p><span><img th:src="\${order.orderIdBarcode}" alt="BarCode 2" style="width: 100%; height: auto; max-height: ${settings.qrCodeSize}px;" /></span></p>
                      <p style="font-size: ${settings.bodyFontSize}px;">
                        <b><span th:if="\${order.shippingLabelId}" th:text="\${order.shippingLabelId}"></span></b>
                      </p>
                    </td>
                  ` : '<td style="width: 50%;"></td>'}
                </tr>
              </table>
            </div>
          ` : `
            <div class="panel text-center" style="margin-top: 4px;">
              <table class="trans-table">
                <tr>
                  <td>
                    <p><span><img style="display: block;width: 100%; height: auto; max-height: ${settings.barcodeHeight}px;" th:src="\${order.barcode}" alt="Barcode" /></span></p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p style="font-size: ${settings.awbFontSize || 14}px; font-weight: 700; letter-spacing: 0.5px;">
                      <b><span th:text="\${order.awbNumber}"></span></b>
                    </p>
                  </td>
                </tr>
              </table>
            </div>
          `}
        ` : ''}
        ${ct('after-barcode')}

        ${settings.showSoldBy || settings.showGSTIN ? `<table>
          <tbody>
            <tr>
              <td>
                <p>${settings.showSoldBy ? '<strong>Sold By:</strong><span th:text="\${order.pickupDetails}"></span>' : ''} ${settings.showSoldBy && settings.showGSTIN ? '|' : ''} ${settings.showGSTIN ? '<strong>GSTIN:</strong><span th:text="\${order.pickupGstin}"></span>' : ''}</p>
              </td>
            </tr>
          </tbody>
        </table>` : ''}
        ${ct('above-item-table')}

        <table class="product">
          <thead>
            <tr>
              ${settings.showDescription ? '<th>Description</th>' : ''}
              ${settings.showSKU ? '<th>SKU</th>' : ''}
              ${settings.showQty ? '<th>Qty</th>' : ''}
            </tr>
          </thead>
          <tbody>
            <tr th:each="item, index : \${items}">
              ${settings.showDescription ? `
              <td>
                <span th:if="\${item.itemName != null}" th:text="\${item.itemName}"></span>
              </td>
              ` : ''}
              ${settings.showSKU ? `
              <td>
                <span th:if="\${item.skuId != null}" th:text="\${item.skuId}"></span>
              </td>
              ` : ''}
              ${settings.showQty ? `
              <td>
                <span th:if="\${item.quantity != null}" th:text="\${item.quantity}"></span>
              </td>
              ` : ''}
            </tr>
          </tbody>
        </table>
        ${ct('below-item-table')}

        <hr />

        <div class="panel block padding-5">
          <div class="black-box">Handover to <span th:text="\${order.courierName}"></span></div>
          <div class="uppercase pull-right"><strong></strong></div>
          <div class="clearFix"></div>
          <div class="margin-top-5">
            ${settings.awbNumber ? `<strong>Tracking ID:</strong> <span th:text="\${order.awbNumber}"></span>` : ''}
          </div>
          ${settings.showBarCode2 && settings.barcodeLayout !== 'side-by-side' ? `<div class="text-center">
            <table class="trans-table">
              <tr>
                <td>
                  <p><span><img th:src="\${order.orderIdBarcode}" alt="Barcode" style="width: 100%; height: auto; max-height: ${settings.qrCodeSize}px;" /></span></p>
                </td>
              </tr>
              <tr>
                <td>
                  <p style="font-size: ${settings.bodyFontSize}px;">
                    <b><span th:if="\${order.shippingLabelId}" th:text="\${order.shippingLabelId}"></span></b>
                  </p>
                </td>
              </tr>
            </table>
          </div>` : ''}
          ${idsAt('bottom')}
        </div>
        ${ct('after-tracking')}

        ${settings.showReturnAddress ? `
        <div class="panel block" style="border-top: 1px solid #000; border-bottom: 1px solid #000; font-size: 8px; padding: 4px;">
          <div style="margin: 0;">
            <p style="margin: 2px 0;">
              <strong>Return Address:</strong>
              <span th:text="\${order.returnName}"></span>
              <span th:text="\${order.returnAddress}"></span>
              <span th:text="\${order.returnPincode}"></span>              
            </p>
            ${settings.showReturnContact ? `
            <p style="margin: 2px 0;">
              <strong>Contact Number:</strong>
              <span th:text="\${order.returnPhone}"></span>
            </p>
            ` : ''}
          </div>
        </div>
        ` : ''}

        ${ct('above-footer')}
        ${settings.bottomText ? `<div style="text-align: right;margin-top:8px">
          <span style="display: inline-block; vertical-align: middle; font-size:9px;margin-right:8px;">${settings.customFooterText}</span>
        </div>` : ''}
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  const handleReset = () => {
    reset(DEFAULT_VALUES);
    enqueueSnackbar('Settings reset to default', { variant: 'success' });
  };

  const handleDownload = () => {
    const htmlTemplate = generateHTMLTemplate(watchedValues);
    const blob = new Blob([htmlTemplate], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `custom_label_template_${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    enqueueSnackbar('Template downloaded successfully!', { variant: 'success' });
  };


  return (
    <>
      <Box sx={{ mb: 2, p: 1.5, borderRadius: '10px', bgcolor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
        <Typography sx={{ fontSize: 12.5, color: '#1E40AF', lineHeight: 1.5 }}>
          Toggle the elements below to build your shipping-label template — the <strong>Live Preview</strong> on the right updates instantly. Download the HTML when you're happy, or upload a ready-made template.
        </Typography>
      </Box>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Left Panel - Settings */}
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: '14px' }}>
              <CardHeader
                title="Template Settings"
                subheader="Show or hide elements, add custom text, and tune sizes"
                avatar={<SettingsIcon color="primary" />}
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent>
                {/* Content — grouped visibility toggles */}
                <SectionAccordion defaultExpanded icon={<ViewModuleIcon fontSize="small" />} title="Content" desc="Choose which blocks appear on the label">
                  <InfoCallout>
                    Toggle any block on or off — the <strong>Live Preview</strong> updates instantly. Switching a block off simply removes it from the printed label.
                  </InfoCallout>
                  <Grid container spacing={2}>
                    {visibleContentGroups.map((g) => (
                      <Grid item xs={12} md={6} key={g.title}>
                        <GroupLabel>{g.title}</GroupLabel>
                        <Box sx={{ border: '1px solid #EAECEF', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                          {g.items.map(([name, label], i) => {
                            const on = !!watchedValues[name];
                            return (
                              <Box
                                key={name}
                                onClick={() => setValue(name, !on)}
                                sx={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  px: 1.5, py: 0.9, cursor: 'pointer',
                                  borderTop: i ? '1px solid #F4F6F9' : 'none',
                                  bgcolor: on ? 'rgba(21,101,192,0.04)' : 'transparent',
                                  '&:hover': { bgcolor: on ? 'rgba(21,101,192,0.08)' : '#F8FAFC' },
                                }}
                              >
                                <Typography sx={{ fontSize: 13, fontWeight: on ? 600 : 400, color: on ? '#1a1a2e' : '#475569' }}>{label}</Typography>
                                <Switch size="small" checked={on} onClick={(e) => e.stopPropagation()} onChange={(e) => setValue(name, e.target.checked)} />
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 2.5 }} />

                  <GroupLabel>Order ID &amp; Order Name position</GroupLabel>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <RHFSelectField
                        name="orderIdPosition"
                        label="Order ID position"
                        options={[{ label: 'Top (header, beside logo)', value: 'top' }, { label: 'Bottom (handover block)', value: 'bottom' }]}
                      />
                      <Hint>Where the Order ID prints. Needs the Order ID toggle above to be on.</Hint>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFSelectField
                        name="orderNamePosition"
                        label="Order Name position"
                        options={[{ label: 'Top (header, beside logo)', value: 'top' }, { label: 'Bottom (handover block)', value: 'bottom' }]}
                      />
                      <Hint>Where the channel Order Name prints. Needs the Order Name toggle above to be on.</Hint>
                    </Grid>
                  </Grid>
                </SectionAccordion>

                {/* Custom Text — your own wording on the label */}
                <SectionAccordion icon={<TextFieldsIcon fontSize="small" />} title="Custom Text" desc="Add your own header, footer and an extra text block">
                  <InfoCallout>
                    Add your own wording to the label. The <strong>header</strong> sits at the top beside the logo, the <strong>footer</strong> prints at the very bottom, and the <strong>custom block</strong> can be dropped at any position you choose below.
                  </InfoCallout>

                  <GroupLabel>Header &amp; Footer</GroupLabel>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <RHFTextField name="customHeaderText" label="Header text" multiline rows={2} placeholder="e.g. your brand name or a tagline" fullWidth />
                      <Hint>Prints at the very top of the label, next to the merchant logo.</Hint>
                    </Grid>
                    <Grid item xs={12}>
                      <RHFTextField name="customFooterText" label="Footer text" placeholder="Powered By Proship" fullWidth />
                      <Hint>Prints at the bottom of the label (e.g. Powered By Proship). Requires the footer block to be on in Content.</Hint>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <GroupLabel>Custom text block</GroupLabel>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <RHFTextField name="customTextAboveFooter" label="Custom text" multiline rows={3} placeholder="e.g. support info, return notes or special instructions" fullWidth />
                      <Hint>An extra line or paragraph you control. Leave it empty to hide the block completely.</Hint>
                    </Grid>
                    <Grid item xs={12}>
                      <RHFSelectField
                        name="customTextPosition"
                        label="Position on label"
                        options={[
                          { label: 'After Header (top)', value: 'after-header' },
                          { label: 'After Shipping Mode', value: 'after-shipping-mode' },
                          { label: 'After Delivery Address', value: 'after-delivery-address' },
                          { label: 'After AWB / Courier Info', value: 'after-awb-info' },
                          { label: 'After Barcode', value: 'after-barcode' },
                          { label: 'Above Item Table', value: 'above-item-table' },
                          { label: 'Below Item Table', value: 'below-item-table' },
                          { label: 'After Tracking / Handover', value: 'after-tracking' },
                          { label: 'Above Footer (default)', value: 'above-footer' },
                        ]}
                      />
                      <Hint>Where the custom block appears in the label flow.</Hint>
                    </Grid>
                  </Grid>

                  <GroupLabel>Style</GroupLabel>
                  <Grid container spacing={2.5} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">Font size: {watchedValues.customTextFontSize}px</Typography>
                      <Slider value={watchedValues.customTextFontSize} onChange={(e, value) => setValue('customTextFontSize', value)} min={6} max={16} step={1} marks valueLabelDisplay="auto" color="info" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">Alignment</Typography>
                      <ToggleButtonGroup value={watchedValues.customTextAlign} exclusive onChange={(e, val) => { if (val) setValue('customTextAlign', val); }} size="small" color="info">
                        <ToggleButton value="left"><AlignLeftIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="center"><AlignCenterIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="right"><AlignRightIcon fontSize="small" /></ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={6}>
                      <RHFCheckbox name="customTextBold" label="Bold text" color="info" />
                    </Grid>
                    <Grid item xs={6}>
                      <RHFCheckbox name="customTextUppercase" label="All caps" color="info" />
                    </Grid>
                  </Grid>
                </SectionAccordion>

                {/* Layout & Sizes */}
                <SectionAccordion icon={<PaletteIcon fontSize="small" />} title="Layout & Sizes" desc="Resize the logo and barcodes, and pick a barcode layout">
                  <InfoCallout>
                    Fine-tune how large the logo and barcodes print, and choose whether the two barcodes <strong>stack</strong> or sit <strong>side by side</strong>. Sizes are in pixels at print resolution.
                  </InfoCallout>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">Logo size: {watchedValues.logoSize}px</Typography>
                      <Slider value={watchedValues.logoSize} onChange={(e, value) => setValue('logoSize', value)} min={20} max={80} step={5} marks valueLabelDisplay="auto" color="info" />
                      <Hint>Size of the merchant logo in the header.</Hint>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">Barcode size: {watchedValues.barcodeHeight}px</Typography>
                      <Slider value={watchedValues.barcodeHeight} onChange={(e, value) => setValue('barcodeHeight', value)} min={20} max={120} step={5} marks valueLabelDisplay="auto" color="info" />
                      <Hint>Height of the main AWB barcode.</Hint>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">Barcode 2 size: {watchedValues.qrCodeSize}px</Typography>
                      <Slider value={watchedValues.qrCodeSize} onChange={(e, value) => setValue('qrCodeSize', value)} min={20} max={120} step={5} marks valueLabelDisplay="auto" color="info" />
                      <Hint>Height of the second (order-ID) barcode.</Hint>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFSelectField name="barcodeLayout" label="Barcode layout" options={[{ label: 'Stacked', value: 'stacked' }, { label: 'Side by Side', value: 'side-by-side' }]} />
                      <Hint>Stacked prints the barcodes one below the other; Side by Side places them in two columns.</Hint>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2">AWB number font size: {watchedValues.awbFontSize}px</Typography>
                      <Slider value={watchedValues.awbFontSize} onChange={(e, value) => setValue('awbFontSize', value)} min={8} max={28} step={1} marks valueLabelDisplay="auto" color="info" />
                      <Hint>Size of the AWB / tracking number printed under the barcode — increase it so it's clearly visible during pickup, delivery and claim checks.</Hint>
                    </Grid>
                  </Grid>
                </SectionAccordion>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel — live preview + actions (sticky) */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <Card sx={{ borderRadius: '14px', mb: 2 }}>
                <CardHeader
                  title="Live Preview"
                  subheader="Updates in real time as you change settings"
                  action={
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={handlePreview} sx={{ textTransform: 'none' }}>
                      Full view
                    </Button>
                  }
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                  subheaderTypographyProps={{ variant: 'caption' }}
                />
                <CardContent sx={{ bgcolor: '#eef1f5', display: 'flex', justifyContent: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Rendered inside an iframe so the label's own global CSS can't leak into the
                      admin page (that leak was causing the top overlap) and it prints true-to-output. */}
                  <Box
                    component="iframe"
                    title="Label preview"
                    srcDoc={fillPreviewData(generateHTMLTemplate(watchedValues))}
                    sx={{ width: '100%', maxWidth: 360, height: 560, border: 'none', borderRadius: '6px', bgcolor: '#fff', boxShadow: '0 6px 20px rgba(15,23,42,0.14)' }}
                  />
                </CardContent>
              </Card>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setOpenUploadModal(true)} sx={{ flex: '1 1 45%', textTransform: 'none', fontWeight: 600 }}>
                  Upload Template
                </Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ flex: '1 1 45%', textTransform: 'none', fontWeight: 600, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Download HTML
                </Button>
                <Button variant="outlined" color="warning" startIcon={<RestartIcon />} onClick={handleReset} sx={{ flex: '1 1 100%', textTransform: 'none', fontWeight: 600 }}>
                  Reset Settings
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
      {/* Preview Modal — snug to a realistic A6 label so there's no dead white space on the sides */}
      <MUIModal
        open={previewOpen}
        handleClose={() => setPreviewOpen(false)}
        maxWidth="sm"
      // title="Template Preview"
      >
        <Box sx={{ py: 1, bgcolor: '#eef1f5', display: 'flex', justifyContent: 'center' }}>
          <Box
            component="iframe"
            title="Label full preview"
            srcDoc={fillPreviewData(generateHTMLTemplate(previewData))}
            sx={{ width: '100%', height: '80vh', border: 'none', borderRadius: '6px', bgcolor: '#fff', boxShadow: '0 8px 28px rgba(15,23,42,0.18)' }}
          />
        </Box>
      </MUIModal>

      <MUIModal
        open={openUploadModal}
        handleClose={() => setOpenUploadModal(false)}
        maxWidth="lg"
        title="Upload Template"
      >
        <Box sx={{ p: 2 }}>
          <LabelConfig showUploadOnly={true} />
        </Box>
      </MUIModal>
    </>
  );
};

export default LabelDIY;
